import { TypedDocumentNode, gql } from '@apollo/client';
import { addDays, isToday, startOfDay } from 'date-fns';
import { ReactNode, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { FootballRivalsCompetitionSortType } from '@sorare/core/src/__generated__/globalTypes';
import Button from '@sorare/core/src/atoms/buttons/Button';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import LoadingIndicator from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { DatePicker } from '@sorare/core/src/components/lobby/DatePicker';
import { glossary } from '@sorare/core/src/lib/glossary';

import { GamesByLeague } from 'components/rivals/GamesByLeague';
import { EmptyState } from 'components/rivals/RivalsMatchesDateLayout/EmptyState';
import { RIVALS_START_DATE } from 'lib/rivals';

import { GameSelection_footballRivalsLeagueGame } from './__generated__/index.graphql';

const Root = styled(Vertical)`
  overflow: hidden;
`;
const LateralPadding = styled.div`
  padding: 0 var(--triple-unit);
`;
const ButtonWrapper = styled.div`
  align-self: center;
`;

type Props<T extends GameSelection_footballRivalsLeagueGame['games'][number]> =
  {
    gamesByLeague?: (Omit<GameSelection_footballRivalsLeagueGame, 'games'> & {
      games: T[];
    })[];
    renderGame: (game: T) => ReactNode;
    loading: boolean;
    fromDate: Date;
    setFromDate: (date: Date) => void;
    setToDate: (date: Date) => void;
    sortBy?: FootballRivalsCompetitionSortType;
    onSortChange?: (sorting: FootballRivalsCompetitionSortType) => void;
    simplifiedLayout?: boolean;
    children?: ReactNode;
  };

export const GameSelection = <
  T extends GameSelection_footballRivalsLeagueGame['games'][number],
>({
  gamesByLeague,
  renderGame,
  loading,
  fromDate,
  setFromDate,
  setToDate,
  sortBy,
  onSortChange,
  simplifiedLayout = false,
  children,
}: Props<T>) => {
  const [showMore, setShowMore] = useState(false);

  if (loading && !gamesByLeague) {
    return <LoadingIndicator />;
  }

  const onEmptyStateClick = (newDate: Date) => {
    setFromDate(newDate);
    setToDate(addDays(newDate, 1));
  };
  const showEmptyState = !loading && !gamesByLeague?.length;
  const hideGamesByLeague = simplifiedLayout && !showMore;

  return (
    <Root gap={2}>
      <LateralPadding>
        <DatePicker
          value={fromDate}
          minDate={RIVALS_START_DATE}
          onChange={(d: Date) => {
            if (isToday(d)) {
              setFromDate(new Date());
            } else {
              setFromDate(startOfDay(d));
            }

            setToDate(startOfDay(addDays(d, 1)));
          }}
          disablePast
        />
      </LateralPadding>
      {children}
      {hideGamesByLeague && (
        <ButtonWrapper>
          <Button
            color="tertiary"
            size="medium"
            onClick={() => setShowMore(true)}
          >
            <FormattedMessage {...glossary.viewMore} />
          </Button>
        </ButtonWrapper>
      )}
      {!!gamesByLeague?.length && !hideGamesByLeague && (
        <GamesByLeague
          sortBy={sortBy}
          onSortChange={onSortChange}
          leagueGames={gamesByLeague}
          renderGame={renderGame}
        />
      )}
      {showEmptyState && (
        <EmptyState date={fromDate} onClick={onEmptyStateClick} />
      )}
    </Root>
  );
};

GameSelection.fragments = {
  footballRivalsLeagueGame: gql`
    fragment GameSelection_footballRivalsLeagueGame on FootballRivalsLeagueGame {
      games {
        slug
      }
      ...GamesByLeague_footballRivalsLeagueGame
    }
    ${GamesByLeague.fragments.footballRivalsLeagueGame}
  ` as TypedDocumentNode<GameSelection_footballRivalsLeagueGame>,
};
